import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
} from '@angular/core';
import { FormGroupDirective } from '@angular/forms';
import { GoogleMapsLoaderService } from '@core/services';
import { AddressPrefix } from '@shared/enums';

@Directive({
  selector: '[appAddressAutocomplete]',
})
export class AddressAutocompleteDirective implements AfterViewInit, OnDestroy {
  private el = inject<ElementRef<HTMLInputElement>>(ElementRef as any);
  private formGroupDir = inject(FormGroupDirective);
  private platformId = inject(PLATFORM_ID);
  private maps = inject(GoogleMapsLoaderService);

  @Input('appAddressAutocomplete') prefix!: AddressPrefix;
  @Input() country: string | string[] = 'us';

  private autocomplete?: google.maps.places.Autocomplete;
  private placeChangedListener?: google.maps.MapsEventListener;

  async ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.el.nativeElement.setAttribute('autocomplete', 'off');
    await this.maps.loadPlaces();

    const autocompleteOptions: google.maps.places.AutocompleteOptions = {
      types: ['address'],
      fields: ['address_components', 'formatted_address', 'geometry'],
      componentRestrictions: { country: this.country },
    };

    const { Autocomplete } = (await google.maps.importLibrary(
      'places',
    )) as google.maps.PlacesLibrary;

    this.autocomplete = new Autocomplete(
      this.el.nativeElement,
      autocompleteOptions,
    );
    this.placeChangedListener = this.autocomplete.addListener(
      'place_changed',
      () => {
        const place = this.autocomplete.getPlace();
        if (!place?.address_components) return;
        const parsed = this.parseAddress(place.address_components);
        const fg = this.formGroupDir.form;
        const k = (s: string) => `${this.prefix}${s}`;
        if (
          this.prefix === AddressPrefix.Mailing ||
          this.prefix === AddressPrefix.Physical
        ) {
          fg.patchValue({
            [k('Street1')]: parsed.street1 ?? '',
            [k('Street2')]: parsed.street2 ?? fg.get(k('Street2'))?.value ?? '',
            [k('City')]: parsed.city ?? '',
            [k('State')]: parsed.state ?? '',
            [k('Zip')]: parsed.zip ?? '',
          });
        } else {
          fg.patchValue({
            [k('StAddr')]: parsed.street1 ?? '',
            [k('City')]: parsed.city ?? '',
            [k('State')]: parsed.state ?? '',
            [k('ZipCode')]: parsed.zip ?? '',
          });
        }
        fg.markAsDirty();
      },
    );
  }

  ngOnDestroy(): void {
    this.placeChangedListener?.remove();
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
      this.autocomplete = undefined;
    }
  }

  private parseAddress(components: google.maps.GeocoderAddressComponent[]) {
    const byType = (t: string) => components.find((c) => c.types.includes(t));
    const getShort = (t: string) => byType(t)?.short_name ?? '';
    const getLong = (t: string) => byType(t)?.long_name ?? '';

    const streetNumber = getShort('street_number');
    const route = getLong('route');
    const subpremise = getShort('subpremise'); // Apt/Unit if present
    const locality =
      getLong('locality') ||
      getLong('postal_town') ||
      getLong('sublocality') ||
      '';
    const state = getShort('administrative_area_level_1');
    const postal = getShort('postal_code');
    const postalSuffix = getShort('postal_code_suffix');

    const street1 = [streetNumber, route].filter(Boolean).join(' ').trim();
    const street2 = subpremise || '';

    const zip = postalSuffix ? `${postal}`.slice(0, 5) : postal; // your control is maxLength 5

    return {
      street1: street1 || undefined,
      street2: street2 || undefined,
      city: locality || undefined,
      state: state || undefined,
      zip: zip || undefined,
    };
  }
}
